import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as ReactToastify from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { SearchableSelect } from './SearchableSelect';
import { useAuth } from '../../context/AuthContext';
import { useGetProductCategoriesQuery } from '../../redux/productCategoryApi';
import { useGetProductSubcategoriesQuery } from '../../redux/productSubcategoryApi';
import { useCreateProductMutation } from '../../redux/productApi';
import {
  IMAGE_MAX_KB,
  PRODUCT_NAME_MAX_LEN,
  PRODUCT_SPS_MAX,
  clampPercentInput,
  sanitizeNonNegativeDecimal,
  sanitizeUnitInput,
  validateImageFilesUnderSize,
  validateOptionalAlphaNumeric,
  validateOptionalNonNegativeNumber,
  validateOptionalPercent,
  validateOptionalUnit,
  validateProductName,
  validateRequired,
} from '../../utils/validation';

function AdminProductAdd() {
  const { area } = useAuth();
  const isAdmin = area === 'admin';
  const navigate = useNavigate();
  const [createProduct, { isLoading }] = useCreateProductMutation();
  const { data: categoriesRes } = useGetProductCategoriesQuery({
    per_page: 500,
    status: true,
  });
  const [form, setForm] = useState({
    name: '',
    category: '',
    subCategory: '',
    batchNo: '',
    mfgDate: '',
    bestBefore: '',
    buffer: '',
    unit: '',
    itemCategory: '',
    sps1: '',
    sps2: '',
    sps3: '',
    itemCode: '',
  });
  const { data: subcategoriesRes } = useGetProductSubcategoriesQuery(
    {
      product_category_id: form.category,
      status: true,
      per_page: 500,
    },
    { skip: !form.category }
  );
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const imageInputRef = useRef(null);
  const [errors, setErrors] = useState({});

  const handleChange = (field) => (e) => {
    const { value } = e.target;
    setForm((prev) => {
      if (field === 'category') {
        return { ...prev, category: value, subCategory: '' };
      }
      if (field === 'batchNo') {
        return {
          ...prev,
          batchNo: String(value ?? '')
            .toUpperCase()
            .replace(/[^A-Za-z0-9]/g, ''),
        };
      }
      if (field === 'buffer' || field === 'itemCategory') {
        return { ...prev, [field]: clampPercentInput(value) };
      }
      if (field === 'unit') {
        return { ...prev, unit: sanitizeUnitInput(value) };
      }
      if (field === 'sps1' || field === 'sps2' || field === 'sps3') {
        return { ...prev, [field]: sanitizeNonNegativeDecimal(value) };
      }
      return { ...prev, [field]: value };
    });
    setErrors((prev) => {
      if (field !== 'category') return { ...prev, [field]: '' };
      return { ...prev, category: '', subCategory: '' };
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const err = validateImageFilesUnderSize(files);
    if (err) {
      ReactToastify.toast.error(err);
      if (imageInputRef.current) imageInputRef.current.value = '';
      setImageFiles([]);
      setErrors((prev) => ({ ...prev, images: err }));
      return;
    }
    setImageFiles(files);
    setErrors((prev) => ({ ...prev, images: '' }));
  };

  const handleRemoveAllImages = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImageFiles([]);
    setErrors((prev) => ({ ...prev, images: '' }));
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (!imageFiles || imageFiles.length === 0) {
      setImagePreviewUrls([]);
      return;
    }
    const urls = imageFiles.map((f) => URL.createObjectURL(f));
    setImagePreviewUrls(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [imageFiles]);

  const selectFieldClass =
    'rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

  const reqLabel = (text) => (
    <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
      <span className="min-w-0 leading-snug">{text}</span>
      <span className="shrink-0 text-base leading-none text-red-600" aria-hidden="true">
        *
      </span>
    </span>
  );

  const validateForm = () => {
    const next = {};

    const nameErr = validateProductName(form.name);
    if (nameErr) next.name = nameErr;

    const catErr = validateRequired(form.category, 'Product Category');
    if (catErr) next.category = catErr;

    const subErr = validateRequired(form.subCategory, 'Product Sub Category');
    if (subErr) next.subCategory = subErr;

    const batchErr = validateOptionalAlphaNumeric(form.batchNo, 'Batch No.');
    if (batchErr) next.batchNo = batchErr;

    const mfgErr = validateRequired(form.mfgDate, 'MFG Date');
    if (mfgErr) next.mfgDate = mfgErr;

    const bestErr = validateRequired(form.bestBefore, 'Use Before / Best Before');
    if (bestErr) next.bestBefore = bestErr;

    const bufferErr = validateOptionalPercent(form.buffer, 'Buffer');
    if (bufferErr) next.buffer = bufferErr;

    const unitErr = validateOptionalUnit(form.unit);
    if (unitErr) next.unit = unitErr;

    const itemCatErr = validateOptionalPercent(form.itemCategory, 'Item Category');
    if (itemCatErr) next.itemCategory = itemCatErr;

    const sps1Err = validateOptionalNonNegativeNumber(form.sps1, 'SPS 1');
    if (sps1Err) next.sps1 = sps1Err;
    const sps2Err = validateOptionalNonNegativeNumber(form.sps2, 'SPS 2');
    if (sps2Err) next.sps2 = sps2Err;
    const sps3Err = validateOptionalNonNegativeNumber(form.sps3, 'SPS 3');
    if (sps3Err) next.sps3 = sps3Err;

    if (!imageFiles.length) {
      next.images = 'Please upload product image';
    } else {
      const imgErr = validateImageFilesUnderSize(imageFiles);
      if (imgErr) next.images = imgErr;
    }

    setErrors(next);
    const ok = Object.keys(next).length === 0;
    if (!ok) {
      const first = Object.values(next).find(Boolean);
      if (first) ReactToastify.toast.error(first);
    }
    return ok;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await createProduct({
        item_name: String(form.name ?? '').trim(),
        product_category_id: form.category ? Number(form.category) : undefined,
        product_subcategory_id: form.subCategory ? Number(form.subCategory) : undefined,
        batch_number: form.batchNo,
        mfg_date: form.mfgDate || undefined,
        best_before: form.bestBefore || undefined,
        buffer: String(form.buffer ?? '').trim() || undefined,
        unit: String(form.unit ?? '').trim() || undefined,
        item_category: String(form.itemCategory ?? '').trim() || undefined,
        sps_1: String(form.sps1 ?? '').trim() || undefined,
        sps_2: String(form.sps2 ?? '').trim() || undefined,
        sps_3: String(form.sps3 ?? '').trim() || undefined,
        item_code: String(form.itemCode ?? '').trim() || undefined,
        status: true,
        images: imageFiles.length ? imageFiles : undefined,
      }).unwrap();
      ReactToastify.toast.success('Product created successfully.');
      navigate('/dashboard/admin/products');
    } catch (err) {
      ReactToastify.toast.error(err?.data?.message || 'Failed to create product.');
    }
  };

  return (
    <DashboardLayout title={"Add Product"}>
      <div className="space-y-4 py-4">
      <div className="flex justify-end">
        <p className="text-sm text-gray-500">
          <Link
            to={isAdmin ? '/dashboard/admin' : '/dashboard/employee'}
            className="text-brand hover:underline"
          >
            Home
          </Link>
          {' / '}
          <Link
            to="/dashboard/admin/products"
            className="text-brand hover:underline"
          >
            Product
          </Link>
          {' / '}
          <span className="text-gray-400">Add Product</span>
        </p>
      </div>

        <form
          className="rounded-2xl bg-white p-4 shadow-sm sm:p-6"
          onSubmit={handleSubmit}
        >
 <h3 className="text-xl font-semibold text-gray-800">
              Product Information
            </h3>

          <div className="my-6 grid gap-4 md:grid-cols-3">
            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">Item Code</span>
              <input
                type="text"
                placeholder="Enter item code"
                value={form.itemCode}
                onChange={handleChange('itemCode')}
                className="rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </label>

            <label className="flex flex-col gap-1">
              {reqLabel('Item Name')}
              <input
                type="text"
                placeholder="Enter name"
                value={form.name}
                onChange={handleChange('name')}
                maxLength={PRODUCT_NAME_MAX_LEN}
                className={`rounded-lg border bg-[#F7E5D5]  placeholder:text-gray-700 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.name ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.name ? (
                <span className="text-xs text-red-600">{errors.name}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              {reqLabel('Product Category')}
              <SearchableSelect
                name="category"
                value={form.category}
                onChange={handleChange('category')}
                placeholder="Select"
                className={`${selectFieldClass} ${errors?.category ? 'border-red-500' : ''}`}
                options={(categoriesRes?.data || []).map((cat) => ({
                  value: String(cat.id),
                  label: cat.name ?? String(cat.id),
                }))}
              />
              {errors?.category ? (
                <span className="text-xs text-red-600">{errors.category}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              {reqLabel('Product Sub Category')}
              <SearchableSelect
                name="subCategory"
                value={form.subCategory}
                onChange={handleChange('subCategory')}
                placeholder="Select"
                disabled={!form.category}
                className={`${selectFieldClass} ${errors?.subCategory ? 'border-red-500' : ''}`}
                options={(subcategoriesRes?.data || []).map((sub) => ({
                  value: String(sub.id),
                  label: sub.name ?? String(sub.id),
                }))}
              />
              {errors?.subCategory ? (
                <span className="text-xs text-red-600">{errors.subCategory}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">Batch No. (As Per Actual)</span>
              <input
                type="text"
                placeholder="Enter Batch No."
                value={form.batchNo}
                onChange={handleChange('batchNo')}
                className={`rounded-lg placeholder:text-gray-700 border bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.batchNo ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.batchNo ? (
                <span className="text-xs text-red-600">{errors.batchNo}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              {reqLabel('MFG Date')}
              <input
                type="date"
                value={form.mfgDate}
                onChange={handleChange('mfgDate')}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.mfgDate ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.mfgDate ? (
                <span className="text-xs text-red-600">{errors.mfgDate}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              {reqLabel('Use Before / Best Before')}
              <input
                type="date"
                value={form.bestBefore}
                onChange={handleChange('bestBefore')}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.bestBefore ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.bestBefore ? (
                <span className="text-xs text-red-600">{errors.bestBefore}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">Buffer (%)</span>
              <input
                type="number"
                step="any"
                min="0"
                max="100"
                placeholder="Enter buffer % (max 100)"
                value={form.buffer}
                onChange={handleChange('buffer')}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.buffer ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.buffer ? (
                <span className="text-xs text-red-600">{errors.buffer}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">Unit</span>
              <input
                type="text"
                placeholder="Enter unit (e.g. Kg, Nos)"
                value={form.unit}
                onChange={handleChange('unit')}
                maxLength={20}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.unit ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.unit ? (
                <span className="text-xs text-red-600">{errors.unit}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">Item Category (%)</span>
              <input
                type="number"
                step="any"
                min="0"
                max="100"
                placeholder="Enter item category % (max 100)"
                value={form.itemCategory}
                onChange={handleChange('itemCategory')}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.itemCategory ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.itemCategory ? (
                <span className="text-xs text-red-600">{errors.itemCategory}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">SPS 1</span>
              <input
                type="number"
                step="any"
                min="0"
                max={PRODUCT_SPS_MAX}
                placeholder="Enter SPS 1"
                value={form.sps1}
                onChange={handleChange('sps1')}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.sps1 ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.sps1 ? (
                <span className="text-xs text-red-600">{errors.sps1}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">SPS 2</span>
              <input
                type="number"
                step="any"
                min="0"
                max={PRODUCT_SPS_MAX}
                placeholder="Enter SPS 2"
                value={form.sps2}
                onChange={handleChange('sps2')}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.sps2 ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.sps2 ? (
                <span className="text-xs text-red-600">{errors.sps2}</span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-700">SPS 3</span>
              <input
                type="number"
                step="any"
                min="0"
                max={PRODUCT_SPS_MAX}
                placeholder="Enter SPS 3"
                value={form.sps3}
                onChange={handleChange('sps3')}
                className={`rounded-lg border bg-[#F7E5D5] px-3 py-2 text-sm placeholder:text-gray-700 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand ${
                  errors?.sps3 ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              {errors?.sps3 ? (
                <span className="text-xs text-red-600">{errors.sps3}</span>
              ) : null}
            </label>
          </div>

          <div className="mb-6">
            <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0 text-sm text-gray-700">
              {reqLabel('Product Image')}
              <span className="text-xs font-normal text-gray-500">
                (max {IMAGE_MAX_KB}KB each)
              </span>
            </div>
            <div
              className={`relative h-40 w-64 overflow-hidden rounded-xl border border-dashed bg-[#F7E5D5] ${
                errors?.images ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <label
                htmlFor="product-image"
                className="flex h-full w-full cursor-pointer items-center justify-center text-center text-xs text-gray-500"
              >
                {imagePreviewUrls?.length ? (
                  <img
                    src={imagePreviewUrls[0]}
                    alt="Selected product"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span>
                    Click to browse
                    <br />
                    or drag &amp; drop file
                  </span>
                )}
              </label>
              <input
                id="product-image"
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                ref={imageInputRef}
                className="hidden"
              />
              {imagePreviewUrls?.length > 0 && (
                <button
                  type="button"
                  onClick={handleRemoveAllImages}
                  className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black/80"
                  aria-label="Remove image"
                  title="Remove image"
                >
                  ×
                </button>
              )}
            </div>
            {imageFiles.length > 0 && (
              <p className="mt-2 text-xs text-gray-600">
                Selected: <span className="font-medium">{imageFiles.length}</span> image(s)
              </p>
            )}
            {errors?.images ? (
              <p className="mt-2 text-xs text-red-600">{errors.images}</p>
            ) : null}
          </div>

          <div className="pt-4 flex justify-start gap-5">
  <button
    type="submit"
    disabled={isLoading}
    className="inline-flex min-w-[140px] items-center justify-center rounded-lg bg-brand px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-800"
  >
    {isLoading ? 'Saving...' : 'Save'}
  </button>

  <button
    type="button"
    onClick={() => navigate('/dashboard/admin/products')}
    className="inline-flex min-w-[140px] items-center justify-center rounded-lg border border-brand bg-white px-8 py-2.5 text-sm font-semibold text-brand hover:bg-gray-100"
  >
    Cancel
  </button>
</div>
        </form>
      </div>
    </DashboardLayout>
  );
}

export default AdminProductAdd;